/**
 * DetailPanel.jsx — dashboard/DetailPanel.jsx
 *
 * Shows extended quote data for the selected symbol plus an intraday
 * direction estimate derived from price-based indicators (no AI call).
 *
 * Direction model (indicator-only, deterministic):
 *   - Pre-market change direction and magnitude
 *   - Position within day range (upper/lower half)
 *   - Relation to previous close
 *   - Volume pace vs 3-month ADV
 *   - Bid/ask skew (micro-structure)
 *
 * Output: Bullish / Mildly Bullish / Neutral / Mildly Bearish / Bearish
 * NOT investment advice — heuristic signal for situational awareness.
 */

import { useMemo } from 'react';
import { spread, classifyPreMarketVolume } from '../../lib/indicators';
import { fmtPrice, fmtPct, fmtVol, colorForPct } from '../../lib/format';

// ── Intraday direction heuristic ─────────────────────────────────────────────
function calcDirection(quote) {
  if (!quote) return null;

  let score = 0; // ranges roughly -4 to +4

  // 1. Pre-market change (strongest intraday signal)
  const prePct = quote.preMarketChangePercent;
  if (prePct != null) {
    if      (prePct >  1.0) score += 2;
    else if (prePct >  0.3) score += 1;
    else if (prePct < -1.0) score -= 2;
    else if (prePct < -0.3) score -= 1;
  }

  // 2. Regular session change vs prev close
  const chgPct = quote.regularMarketChangePercent;
  if (chgPct != null) {
    if      (chgPct >  0.8) score += 1.5;
    else if (chgPct >  0.2) score += 0.5;
    else if (chgPct < -0.8) score -= 1.5;
    else if (chgPct < -0.2) score -= 0.5;
  }

  // 3. Position in day range (price near high = bullish momentum)
  const hi = quote.regularMarketDayHigh;
  const lo = quote.regularMarketDayLow;
  const px = quote.regularMarketPrice;
  if (hi && lo && px && hi !== lo) {
    const rangePct = (px - lo) / (hi - lo); // 0=low, 1=high
    if      (rangePct > 0.75) score += 1;
    else if (rangePct > 0.5)  score += 0.3;
    else if (rangePct < 0.25) score -= 1;
    else if (rangePct < 0.5)  score -= 0.3;
  }

  // 4. Volume pace (high volume amplifies direction signal)
  const vol  = quote.regularMarketVolume;
  const adv  = quote.averageDailyVolume3Month;
  if (vol && adv) {
    // Rough half-day pace: if vol > 60% ADV midday, it's elevated
    const volRatio = vol / adv;
    if (volRatio > 0.8) score *= 1.3;   // amplify on heavy vol
    else if (volRatio < 0.2) score *= 0.7; // dampen on thin vol
  }

  // 5. Bid/ask micro-structure skew
  const bid = quote.bid, ask = quote.ask;
  if (bid && ask && bid > 0 && ask > 0) {
    const mid = (bid + ask) / 2;
    const px2 = quote.regularMarketPrice || mid;
    // If price is above mid → buyers lifting; below mid → sellers hitting
    const skew = (px2 - mid) / mid;
    if      (skew >  0.001) score += 0.4;
    else if (skew < -0.001) score -= 0.4;
  }

  // Map score to label
  const clamp = Math.max(-4, Math.min(4, score));
  let label, color, icon;
  if      (clamp >= 2)   { label = 'Bullish';       color = 'var(--up)';   icon = '↑↑'; }
  else if (clamp >= 0.6) { label = 'Mildly Bullish';color = 'var(--up)';   icon = '↑';  }
  else if (clamp > -0.6) { label = 'Neutral';        color = 'var(--muted)';icon = '→';  }
  else if (clamp > -2)   { label = 'Mildly Bearish'; color = 'var(--down)'; icon = '↓';  }
  else                   { label = 'Bearish';         color = 'var(--down)'; icon = '↓↓'; }

  return { label, color, icon, score: clamp };
}

// ── Component ────────────────────────────────────────────────────────────────
export default function DetailPanel({ symbol, quote }) {
  if (!symbol) {
    return <div className="empty">Select a symbol from the watchlist.</div>;
  }

  const sp  = spread(quote?.bid, quote?.ask);
  const pmv = classifyPreMarketVolume(quote?.preMarketVolume, quote?.averageDailyVolume3Month);
  const dir = useMemo(() => calcDirection(quote), [quote]);

  const cells = [
    { lbl: 'Last',       val: fmtPrice(quote?.regularMarketPrice),        color: colorForPct(quote?.regularMarketChangePercent) },
    { lbl: 'Change',     val: fmtPct(quote?.regularMarketChangePercent),   color: colorForPct(quote?.regularMarketChangePercent) },
    { lbl: 'Pre-Mkt $',  val: fmtPrice(quote?.preMarketPrice),             color: colorForPct(quote?.preMarketChangePercent) },
    { lbl: 'Pre-Mkt %',  val: fmtPct(quote?.preMarketChangePercent),       color: colorForPct(quote?.preMarketChangePercent) },
    { lbl: 'Pre Vol',    val: fmtVol(quote?.preMarketVolume),              sub: pmv.pct != null ? `${pmv.pct.toFixed(1)}% ADV` : null, badge: pmv.level },
    { lbl: 'Avg Vol 3M', val: fmtVol(quote?.averageDailyVolume3Month) },
    { lbl: 'Bid',        val: quote?.bid ? `${fmtPrice(quote.bid)} ×${quote.bidSize ?? '?'}` : '—' },
    { lbl: 'Ask',        val: quote?.ask ? `${fmtPrice(quote.ask)} ×${quote.askSize ?? '?'}` : '—' },
    { lbl: 'Spread',     val: sp ? sp.abs.toFixed(3) : '—',               sub: sp ? `${sp.pct.toFixed(3)}%` : null },
    { lbl: 'Day Range',  val: (quote?.regularMarketDayLow && quote?.regularMarketDayHigh) ? `${fmtPrice(quote.regularMarketDayLow)}–${fmtPrice(quote.regularMarketDayHigh)}` : '—' },
    { lbl: 'Open',       val: fmtPrice(quote?.regularMarketOpen) },
    { lbl: 'Prev Close', val: fmtPrice(quote?.regularMarketPreviousClose) },
    { lbl: '52W High',   val: fmtPrice(quote?.fiftyTwoWeekHigh) },
    { lbl: '52W Low',    val: fmtPrice(quote?.fiftyTwoWeekLow) },
  ];

  // Day range fill pct for visual bar
  const rangeFill = (() => {
    const hi = quote?.regularMarketDayHigh, lo = quote?.regularMarketDayLow, px = quote?.regularMarketPrice;
    if (!hi || !lo || !px || hi === lo) return null;
    return Math.max(0, Math.min(100, ((px - lo) / (hi - lo)) * 100));
  })();

  return (
    <div style={{ height: '100%', overflowY: 'auto' }}>
      {/* Symbol header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        <span style={{ fontWeight: 700, fontSize: 15, fontFamily: 'var(--mono)' }}>{symbol}</span>
        {quote?.shortName && <span className="faint" style={{ fontSize: 11 }}>{quote.shortName}</span>}
        {quote?.marketState && (
          <span className={`mstate ${quote.marketState}`} style={{ marginLeft: 'auto' }}>{quote.marketState}</span>
        )}
      </div>

      {/* ── Direction estimate ─────────────────────────────────────────────── */}
      {dir && (
        <div style={{
          margin: '8px 12px',
          background: 'var(--bg-elev)',
          border: `1px solid ${dir.color}44`,
          borderLeft: `3px solid ${dir.color}`,
          borderRadius: 6,
          padding: '8px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}>
          <span style={{ fontSize: 18, fontFamily: 'var(--mono)', fontWeight: 700, color: dir.color, width: 24, textAlign: 'center' }}>
            {dir.icon}
          </span>
          <div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 700, color: dir.color, letterSpacing: '.06em', textTransform: 'uppercase' }}>
              {dir.label}
            </div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--faint)', marginTop: 1, letterSpacing: '.05em' }}>
              INTRADAY EST · INDICATOR-BASED · NOT ADVICE
            </div>
          </div>

          {/* Mini confidence bar */}
          <div style={{ marginLeft: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
            <div style={{ display: 'flex', gap: 2 }}>
              {[-2, -1, 0, 1, 2].map(seg => {
                const active = dir.score >= 0
                  ? seg >= 0 && seg < Math.ceil(dir.score / 1.2)
                  : seg <= 0 && seg > Math.floor(dir.score / 1.2);
                const isNeg = dir.score < 0;
                return (
                  <div key={seg} style={{
                    width: 10, height: 6, borderRadius: 2,
                    background: active ? (isNeg ? 'var(--down)' : 'var(--up)') : 'var(--bg-hover)',
                    border: '1px solid var(--border)',
                  }} />
                );
              })}
            </div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 8, color: 'var(--faint)' }}>SIGNAL</div>
          </div>
        </div>
      )}

      {/* Day range visual bar */}
      {rangeFill !== null && (
        <div style={{ margin: '0 12px 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--muted)', width: 28 }}>LO</span>
          <div style={{ flex: 1, height: 5, background: 'var(--bg-hover)', borderRadius: 3, position: 'relative', border: '1px solid var(--border)' }}>
            <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${rangeFill}%`, background: 'var(--accent)', borderRadius: 3, transition: 'width .4s' }} />
            <div style={{ position: 'absolute', top: -3, bottom: -3, left: `${rangeFill}%`, width: 2, background: 'var(--text)', borderRadius: 1, transform: 'translateX(-50%)' }} />
          </div>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--muted)', width: 24, textAlign: 'right' }}>HI</span>
        </div>
      )}

      {/* Stat grid */}
      <div className="detail-grid">
        {cells.map(({ lbl, val, color, sub, badge }) => (
          <div key={lbl} className="detail-cell">
            <div className="dc-lbl">{lbl}</div>
            <div className="dc-val" style={{ color: color || 'var(--text)' }}>{val}</div>
            {sub   && <div className="dc-sub">{sub}</div>}
            {badge && <div style={{ marginTop: 3 }}><span className={`badge ${badge}`}>{badge}</span></div>}
          </div>
        ))}
      </div>
    </div>
  );
}
